import { useVolumeBalance } from "@/hooks/use-volume-balance";
import { VolumeDistributionChart } from "./VolumeDistributionChart";
import { ImbalanceAlerts } from "./ImbalanceAlerts";
import { VolumeStatsGrid } from "./VolumeStatsGrid";
import { MuscleGroupTable } from "./MuscleGroupTable";
import { VolumeTrendChart } from "./VolumeTrendChart";
import { MuscleHeatmap } from "./MuscleHeatmap";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface VolumeBalanceAnalyticsProps {
  clientId?: string;
  weeksBack?: number;
}

export function VolumeBalanceAnalytics({ clientId, weeksBack = 4 }: VolumeBalanceAnalyticsProps) {
  const { analysis, trends, loading, error } = useVolumeBalance({ clientId, weeksBack });

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-0">
              <CardContent className="pt-6">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="chart-height-md w-full" />
          </CardContent>
        </Card>
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="chart-height-md w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="chart-height-md w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return null;
  }

  return (
    <div className="space-y-6">
      <VolumeStatsGrid analysis={analysis} weeksAnalyzed={weeksBack} />
      
      <VolumeTrendChart weeklyTrends={trends.weekly} monthlyTrends={trends.monthly} />
      
      <div className="grid md:grid-cols-2 gap-6">
        <MuscleHeatmap muscleGroups={analysis.muscleGroups} />
        <ImbalanceAlerts imbalances={analysis.imbalances} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <VolumeDistributionChart muscleGroups={analysis.muscleGroups} />
        <MuscleGroupTable muscleGroups={analysis.muscleGroups} />
      </div>
    </div>
  );
}
